import { type FC, useEffect } from 'react';
import { Trans } from 'react-i18next';

import { useMessageDraft } from '@/features/chat/contexts/MessageDraftContext';
import { CTA } from '@/features/w2w/components/CTA/CTA';
import { FAQ } from '@/features/w2w/components/FAQ/FAQ';
import { Footer } from '@/features/w2w/components/Footer/Footer';
import { Header } from '@/features/w2w/components/Header/Header';
import { LatestResult } from '@/features/w2w/components/LatestResult/LatestResult';
import { Testimonial } from '@/features/w2w/components/Testimonial/Testimonial';
import { UseCases } from '@/features/w2w/components/UseCases/UseCases';
import { USE_CASES } from '@/features/w2w/components/UseCases/data';
import { useLandingPage } from '@/features/w2w/hooks/landing-page';
import { useLandingRedirect } from '@/features/w2w/hooks/landing-redirect';
import { Hero } from '@/features/w2w/peoplesearch/components/Hero/Hero';
import { preloadDeepsearchMapAnimation } from '@/features/w2w/peoplesearch/utils/preload-deepsearch-map-animation';

import styles from './PeopleSearchRoute.module.scss';

export const PeopleSearchRoute: FC = () => {
  const { searchText, setSearchText } = useMessageDraft();
  const { handleSearch } = useLandingRedirect();

  useLandingPage();

  useEffect(() => {
    preloadDeepsearchMapAnimation();
  }, []);

  return (
    <>
      <Header variant="alternate" />
      <main className={styles.content}>
        <Hero
          searchText={searchText}
          setSearchText={setSearchText}
          handleSearch={handleSearch}
        />
        <UseCases
          className={styles.section}
          variant="alternate"
          items={USE_CASES}
          title={
            <Trans i18nKey="useCases.alternate.title">
              Your online reputation matters
            </Trans>
          }
          subtitle={
            <Trans i18nKey="useCases.alternate.subtitle">
              Discover what the internet says about you or someone else — social
              media, news mentions, photos, hidden profiles, negative press, and
              more — all in one place. Use it to safeguard your identity,
              understand how you appear online, or verify someone before you
              trust them.
            </Trans>
          }
        />
        <LatestResult
          className={styles.section}
          title={<Trans i18nKey="latestResult.title">Latest Results</Trans>}
          subtitle={
            <Trans i18nKey="latestResult.subtitle">
              Here are some of the latest results from our search service.
            </Trans>
          }
          variant="alternate"
        />
        <Testimonial
          className={styles.section}
          variant="alternate"
          title={
            <Trans i18nKey="testimonial.alternate.title">
              Thousands are uncovering the truth
            </Trans>
          }
          subtitle={
            <Trans i18nKey="testimonial.alternate.subtitle">
              See how real users are revealing connections, protecting loved
              ones, and satisfying their curiosity — all with just one search.
            </Trans>
          }
        />
        <div className={styles['cta-container']}>
          <CTA variant="alternate" />
        </div>
        <FAQ className={styles.section} variant="alternate" />
      </main>
      <Footer variant="alternate" />
    </>
  );
};
